import React, {useState, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {currentResource} from '../../../util/utils';
import {useSelector} from 'react-redux';
import {flatten, isEmpty} from 'lodash';
import AssignmentsList from './AssignmentsList';
import Button from '../BaseComponentWrappers/MotifButton';
import Utility from '../../../util/utilityfunctions';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import {toggleModal} from '../Modal/ReactiveModal';
import HandOff from './HandOff/HandOff';
import {useDocumentId, useUserPreferences} from '../../../util/customHooks';
export default function ManageUserAssignments({
	engagementId,
	taskId,
	assignments,
	taskTypeId,
	taskStatusId,
	timePhaseId,
	setClickOutside,
	styleName,
	onAssigmentCompleted
}) {
	const timePhases = useSelector((store) => store.timePhases);
	const userPreferences = useUserPreferences();
	const docId = useDocumentId();

	const timePhasesFlat = flatten(timePhases?.map((tp) => tp.timePhases));
	const taskTimePhase = timePhasesFlat.find((tp) => tp.id === timePhaseId);
	let timePhaseName = taskTimePhase?.periodName;
	let timePhaseEndDate = taskTimePhase?.dueDate;

	const hasAssignments = useMemo(() => {
		return assignments && !isEmpty(assignments);
	}, [assignments]);

	function onHandOffClick(event) {
		event.preventDefault();
		event.stopPropagation();

		setClickOutside && setClickOutside();

		toggleModal({
			modalsize: 'md',
			showButtons: true,
			title: currentResource.handOffModal.title,
			hideConfirmButton: false,
			hideCancelButton: false,
			showSaveAndCreateNewButton: false,
			closeTitle: currentResource.handOffModal.closeTitle,
			confirmBtnTitle: currentResource.handOffModal.confirmButton,
			closeBtnTitle: currentResource.handOffModal.closeTitle,
			disableClose: false,
			okTitle: currentResource.handOffModal.confirmButton,
			modalContainerClass: 'md-custom',
			component: <HandOff engagementId={engagementId} taskId={taskId} assignments={assignments} documentId={docId} />,
			isAresView: true
		});
	}

	return (
		<StyledManageUserAssignments className={`StyledManageUserAssignments ${styleName}`}>
			<section className="manage-assignment-header">
				<h5 className="manage-header-title">{currentResource.reviewerAssignments.taskAssigmentStatusHeader}</h5>
				<section className="manageAssignmentTimePhaseWrapper">
					<section className="manageAssignmentTimePhase">
						<span className="manageAssignmentTimePhaseText">{currentResource.reviewerAssignments.timePhaseName}</span>
						<span className="manageAssignmentTimePhaseValue">
							<EllipsesControl content={timePhaseName} tooltip={timePhaseName} noOfLines={1} />
						</span>
					</section>
					<section className="manageAssignmentTimePhase">
						<span className="manageAssignmentTimePhaseText">
							{currentResource.reviewerAssignments.timePhaseEndDate}
						</span>
						<span className="manageAssignmentTimePhaseValue">
							{Utility.getFormattedDateValue(userPreferences, timePhaseEndDate)}
						</span>
					</section>
				</section>
			</section>
			<section className="manageAssignmentList customScrollbar">
				{hasAssignments && (
					<AssignmentsList
						taskAssignments={assignments}
						taskTypeId={taskTypeId}
						taskId={taskId}
						userPreferences={userPreferences}
						engagementId={engagementId}
						taskStatusId={taskStatusId}
						timePhase={!isEmpty(assignments) && assignments.taskTimePhase}
						onAssigmentCompleted={onAssigmentCompleted}
						dueDate={timePhaseEndDate}
					/>
				)}
				<div className={!hasAssignments ? 'no-assignment-message' : 'no-assignment-message hide-message'} />
			</section>
			<section className="manage-assignment-footer">
				{hasAssignments && (
					<Button
						variant="secondary"
						onClick={onHandOffClick}
						showLabel
						buttonLabel={currentResource.reviewerAssignments.handoffButton}
						size="small"
					/>
				)}
			</section>
		</StyledManageUserAssignments>
	);
}

ManageUserAssignments.propTypes = {
	taskId: PropTypes.string,
	engagementId: PropTypes.number,
	taskTypeId: PropTypes.number,
	taskStatusId: PropTypes.number,
	onAssigmentCompleted: PropTypes.func
};

const StyledManageUserAssignments = styled.section`
	width: var(--px-600);
	.inlineview {
		margin-top: var(--px-15);
	}
	.manage-assignment-header {
		height: var(--px-84);
		padding: var(--px-16);
		display: flex;
		justify-content: space-between;
		flex-direction: column;
		border-bottom: var(--px-1) solid var(--neutrals-100);
		.manage-header-title {
			font-weight: 400;
			margin: 0;
			width: auto;
			padding-top: var(--px-3);
			font-size: var(--px-16);
			color: var(--neutrals-900);
			line-height: var(--px-24);
		}
		.manageAssignmentTimePhaseWrapper {
			display: flex;
			line-height: normal;
			margin: var(--px-10) 0;
			justify-content: space-between;
			.manageAssignmentTimePhase {
				display: flex;
				margin-right: var(--px-10);
				.manageAssignmentTimePhaseText {
					font-size: var(--px-14);
					color: var(--neutrals-700);
					font-weight: 300;
					line-height: var(--px-20);
				}
				.manageAssignmentTimePhaseValue {
					font-size: var(--px-14);
					color: var(--neutrals-900);
					margin-left: var(--px-5);
					max-width: var(--px-400);
					font-weight: 300;
					line-height: var(--px-20);
				}
			}
		}
		.manage-assignment-link {
			margin-left: auto;
			button {
				font-weight: normal;
				font-size: var(--px-15);
				color: var(--blue-600);
				&:hover {
					text-decoration: underline;
				}
			}
		}
	}
	.manage-assignment-footer {
		height: var(--px-84);
		display: flex;
		justify-content: flex-end;
		padding: var(--px-16) var(--px-15);
		border-top: var(--px-1) solid var(--neutrals-100);
		background: var(--neutrals-25);
		align-items: center;
		button {
			width: var(--px-113);
			height: var(--px-44) !important;
			span {
				font-size: var(--px-16);
				font-weight: 400;
				line-height: var(--px-24);
			}
		}
	}
	.timePhase-assignment {
		&.timePhase-bar {
			padding: 0 var(--px-15) var(--px-5);
			background: var(--neutrals-100);
		}
		.timephase-detail {
			.ellipses {
				max-width: var(--px-300);
			}
		}
	}

	.no-assignment-message {
		padding: var(--px-10);
		font-size: var(--px-12);
		& > section {
			display: inline-block;
			.create-assignment {
				margin-left: 0;
				color: var(--blue-600);
			}
		}
		&.hide-message {
			display: none;
		}
	}

	.no-assign-time-phase {
		padding: var(--px-5) var(--px-15) var(--px-5);
		border-top: var(--px-1) solid var(--neutrals-300);
		font-size: var(--px-13);
	}
`;
